import { createFileRoute, notFound } from "@tanstack/react-router";
import { Phone, ArrowRight, Plus } from "lucide-react";

import { services, cities, serviceHref, cityHref, type ServiceSlug } from "@/lib/site";
import { servicesContent } from "@/lib/content";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, serviceSchema, breadcrumbSchema, faqSchema } from "@/lib/schema";
import { IMAGES } from "@/lib/images";
import { Reveal } from "@/components/site/Reveal";
import { WaveDivider } from "@/components/site/WaveDivider";
import { SectionHeading } from "@/components/site/SectionHeading";
import { LinkButton } from "@/components/site/Buttons";
import { CtaBand, CityChips } from "@/components/site/CTA";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero, CheckList, ProseList } from "@/components/site/PageBits";
import { BUSINESS } from "@/lib/site";

export const Route = createFileRoute("/services/$slug")({
  params: {
    parse: (raw) => raw as { slug: string },
  },
  loader: ({ params }) => {
    const content = servicesContent[params.slug as ServiceSlug];
    if (!content) throw notFound();
    return { content };
  },
  head: ({ loaderData }) => {
    const c = loaderData?.content;
    if (!c) return {};
    return {
      meta: [
        { title: c.metaTitle },
        { name: "description", content: c.metaDescription },
        { property: "og:title", content: c.metaTitle },
        { property: "og:description", content: c.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:image", content: IMAGES.coverBanner },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: IMAGES.coverBanner },
      ],
    };
  },
  component: ServiceDetail,
});

function ServiceDetail() {
  const { content: c } = Route.useLoaderData();
  const others = services.filter((s) => s.slug !== c.slug);

  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={serviceSchema({ slug: c.slug, name: c.name, short: c.name, blurb: c.intro } as any)}
      />
      <JsonLd data={faqSchema(c.miniFaq)} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: c.name, path: `/services/${c.slug}` },
        ])}
      />

      <InnerHero
        eyebrow="Service"
        title={c.h1}
        subtitle={c.intro}
        image={c.heroImage}
        imageAlt={c.heroAlt}
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Services", path: "/services" },
              { name: c.name, path: `/services/${c.slug}` },
            ]}
          />
        </div>
      </InnerHero>

      {/* What's included + Add-on services */}
      <section className="px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-2xl font-bold text-ink md:text-3xl">What's included</h2>
            <p className="mt-3 text-muted-foreground text-pretty">
              A clear, checklist-style look at what this service covers. We can always tailor it to
              your space — tell us what you'd like to focus on.
            </p>
            <div className="mt-6">
              <CheckList items={c.whatsIncluded} />
            </div>
            {c.addOns && c.addOns.length > 0 && (
              <div className="mt-8">
                <p className="eyebrow">Add-on services</p>
                <ul className="mt-4 space-y-3">
                  {c.addOns.map((a) => (
                    <li
                      key={a.label}
                      className="flex gap-3 rounded-2xl border border-blush bg-blush/60 p-4"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Plus className="h-4 w-4" />
                      </span>
                      <span className="flex-1">
                        <span className="font-semibold text-ink">{a.label}</span>
                        <span className="text-muted-foreground"> — {a.detail}</span>
                      </span>
                      <span className="mt-0.5 shrink-0 rounded-full bg-gold/20 px-2.5 py-0.5 text-xs font-semibold text-gold">
                        Add-on
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-sm text-muted-foreground">
                  Add-ons are quoted separately.{" "}
                  <a
                    href="/contact#quote"
                    className="font-semibold text-primary underline-offset-4 hover:underline"
                  >
                    Request a quote
                  </a>
                  .
                </p>
              </div>
            )}
          </Reveal>
          <Reveal variant="blur" delay={80}>
            <h2 className="text-2xl font-bold text-ink md:text-3xl">Who it's for</h2>
            <p className="mt-3 text-muted-foreground text-pretty">
              This service is a great fit when you're in any of these situations:
            </p>
            <div className="mt-6">
              <ProseList items={c.whoFor} />
            </div>
          </Reveal>
        </div>
      </section>

      <WaveDivider variant="blush" />

      {/* Why it matters */}
      <section className="px-4 py-12 md:px-6 md:py-16">
        <Reveal className="mx-auto max-w-3xl text-center">
          <SectionHeading eyebrow="Why It Matters" title={`Why ${c.name.toLowerCase()} matters`} />
        </Reveal>
        <div className="mx-auto mt-10 max-w-3xl">
          <ProseList items={c.whyItMatters} />
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="bg-secondary/60 px-4 py-12 md:px-6 md:py-16">
        <Reveal className="mx-auto max-w-3xl">
          <h2 className="text-2xl font-bold text-ink md:text-3xl">{c.name} — quick questions</h2>
          <div className="mt-6 divide-y divide-border rounded-2xl border border-border bg-card">
            {c.miniFaq.map((q) => (
              <details key={q.question} className="group p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold text-ink">
                  {q.question}
                  <span className="text-primary transition-transform duration-300 group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground md:text-base text-pretty">
                  {q.answer}
                </p>
              </details>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Other services */}
      <section className="px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <SectionHeading eyebrow="More Services" title="Other ways we can help" />
        </Reveal>
        <div className="mx-auto mt-8 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((s) => (
            <a
              key={s.slug}
              href={serviceHref(s.slug)}
              className="lift-card group block rounded-2xl border border-border bg-card p-5"
            >
              <h3 className="text-base font-bold text-ink">{s.name}</h3>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{s.blurb}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Learn more{" "}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Where we serve */}
      <section className="bg-secondary/60 px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <SectionHeading
            eyebrow="Service Areas"
            title="Where we serve"
            intro="We're based in Wetumpka and serve the surrounding River Region."
          />
        </Reveal>
        <CityChips />
      </section>

      <CtaBand
        title={`Ready for ${c.name.toLowerCase()}?`}
        subtitle="Tell us about your space and we'll put together a quote. No obligation — just a pretty clean waiting to happen."
      />
    </>
  );
}
